import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api, setActiveContextId } from '@services/api'
import { useAuth } from '@context/AuthContext'

export interface UserBizContext {
  id: number
  name: string
  display_name: string
  description?: string
}

interface UserContextType {
  activeContext: UserBizContext | null
  availableContexts: UserBizContext[]
  switchContext: (id: number) => void
  isLoading: boolean
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const useUserContext = (): UserContextType => {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUserContext must be used within UserContextProvider')
  return ctx
}

const ACTIVE_CONTEXT_STORAGE_KEY = 'dalify_active_context_id'

export const UserContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()
  const [availableContexts, setAvailableContexts] = useState<UserBizContext[]>([])
  const [activeContext, setActiveContextState] = useState<UserBizContext | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return

    if (!isAuthenticated) {
      setAvailableContexts([])
      setActiveContextState(null)
      setActiveContextId(null)
      setIsLoading(false)
      return
    }

    fetchContexts()
  }, [isAuthenticated, authLoading, user])

  // Keep the interceptor in sync for every other path that changes the active
  // context (initial load, restore from sessionStorage, sign-out). switchContext
  // sets it synchronously as well — see the note there.
  useEffect(() => {
    setActiveContextId(activeContext?.id ?? null)
  }, [activeContext])

  const fetchContexts = async () => {
    try {
      setIsLoading(true)
      const currentUser = Array.isArray(user?.contexts) ? user : await api.getCurrentUser()
      const contexts: UserBizContext[] = currentUser.contexts ?? []
      setAvailableContexts(contexts)

      // Restore previously selected context from sessionStorage, fallback to first
      const storedId = sessionStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY)
      const storedContext = storedId ? contexts.find((c) => c.id === Number(storedId)) : null
      setActiveContextState(storedContext ?? contexts[0] ?? null)
    } catch {
      setAvailableContexts([])
      setActiveContextState(null)
    } finally {
      setIsLoading(false)
    }
  }

  const switchContext = (id: number) => {
    const ctx = availableContexts.find((c) => c.id === id)
    if (!ctx) return
    // Update the request header SYNCHRONOUSLY, before any re-render.
    //
    // The effect below also does this, but effects in child components run
    // before those in the provider — so a page reacting to the context change
    // would fire its reloads while the interceptor still carried the previous
    // context id, and render the old context's data as if it were the new one.
    setActiveContextId(id)
    setActiveContextState(ctx)
    sessionStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, String(id))
  }

  return (
    <UserContext.Provider value={{ activeContext, availableContexts, switchContext, isLoading }}>
      {children}
    </UserContext.Provider>
  )
}
